<template>
    <div class="card">
        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                title="Location List"
                btnTitle="Add New"
                :showAddNew="true"
                @onClickAddNewButton="onClickAddNewButton"
            />
        </div>

      <div class="my-2 px-2">
        <div class="row">
          <div class="col-12 col-md-3">
            <label class="col-form-label">Division</label>
            <AsyncSelect
                placeholder="Select Division"
                v-model="division"
                :api-service="fetchDropdownLocationList"
                :additional-query="{ type: 'division'}"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="col-form-label">District</label>
            <AsyncSelect
                placeholder="Select District"
                v-model="district"
                :api-service="fetchDropdownLocationList"
                :additional-query="{ type: 'district'}"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="col-form-label">Thana</label>
            <AsyncSelect
                placeholder="Select Thana"
                v-model="thana"
                :api-service="fetchDropdownLocationList"
                :additional-query="{ type: 'thana'}"
            />
          </div>
          <div class="col-12 col-md-3">
            <label class="col-form-label">Area</label>
            <AsyncSelect
                placeholder="Select Area"
                v-model="area"
                :api-service="fetchDropdownLocationList"
                :additional-query="{ type: 'area'}"
            />
          </div>

          <div class="col-2 mt-3">
            <button
                style="min-width: 64px;"
                @click="onClickToGo"
                class="btn btn-primary btn-block waves-effect waves-float waves-light"
            >Go
            </button>
          </div>
        </div>

        <div class="col-12">
          <div class="d-flex justify-content-end px-2">
            <button
                @click="exportList"
                class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
            >
              Export
            </button>
          </div>
        </div>
      </div>

        <div class="col-12 px-2 mt-2 mb-4">
          <div class="card p-2" style="min-height: 200px">
            <div class="table-responsive">
              <table class="table">
                <thead>
                <tr>
                  <th>Division</th>
                  <th>District</th>
                  <th>Thana</th>
                  <th>Area</th>
                  <th class="text-center">Status</th>
                  <th>Action</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(item, i) in tableItems" :key="i">
                  <td>{{ item.division }}</td>
                  <td>{{ item.district }}</td>
                  <td>{{ item.thana }}</td>
                  <td>{{ item.area }}</td>
                  <td class="text-center text-capitalize"> {{ item.status }}</td>
                  <td>
                    <button class="btn btn-primary btn-sm"
                            @click="goToEdit(item)"
                            style="cursor: pointer"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
              <p v-if="tableItems.length < 0" class="text-center mt-5">No data</p>
            </div>
          </div>
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
        </div>

        <Loader v-if="loading"/>

    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import handleOrganizationSetting from "@/services/modules/organizationSettings";
import { computed } from 'vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const division = ref('')
const district = ref('')
const thana = ref('')
const area = ref('')
const tableItems = ref([]);
const params = ref({
    offset: 10,
});

const token = localStorage.getItem('token');

const {
  fetchLocationList,
  fetchDropdownLocationList
} = handleOrganizationSetting()

const onClickToGo = () => {
  getLocationList();
}

function onClickAddNewButton() {
  router.push({name: `location-add`, params: route.params, query: route.query})
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
};

const setPagination = (data) => {
    setPaginationData({
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    })
}

const exportList = () => {
  let url = `${process.env.VUE_APP_BASE_URL}/api/locations${getQuery()}&_token=${token}&is_export=1`;
  let a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
}

const onPageChange = async (page) => {
    let routeQuery = Object.assign({}, route.query)
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getLocationList();
}

const getLocationList = async () => {
    const query = getQuery();
    loading.value = true
    tableItems.value = []

    await fetchLocationList(query).then(res => {
        if (!res.status){
            store.commit("resetPagination")
            return
        }

        tableItems.value = res.data.data;
        setPagination(res.data)
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const getQuery = () => {
    let query = `?company_id=${route.params.companyId}`;
    if (route.query.page) query += '&page=' + route.query.page;
    if (division.value) query += '&division=' + division.value.id;
    if (district.value) query += '&district=' + district.value.id;
    if (thana.value) query += '&thana=' + thana.value.id;
    if (area.value) query += '&area=' + area.value.id;
    query += '&offset=' + params.value.offset
    return query;
}

const goToEdit = (item) => {
  let param = {
    companyId: route.params.companyId,
    moduleId: route.params.moduleId,
    menuId: route.params.menuId,
    pageId: route.params.pageId
  }

  let query = route.query;
  query.locationId = item.id;

  router.push({
    name:  'location-add',
    params: param,
    query: query
  })
}

onMounted(() => {
    Promise.all([
      getLocationList(),
    ])
})
</script>
